import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatNumber, formatPercentChange } from '@/lib/format'
import { cn } from '@/lib/cn'
import {
  useMarket,
  type MarketCategory,
  type MarketInstrument,
} from '@/contexts/MarketContext'
import { useNavigation } from '@/contexts/NavigationContext'

type SortKey = 'ticker' | 'change' | 'volume'
type SortDir = 'asc' | 'desc'
type FilterId = MarketCategory | 'all' | 'favorites'

function priceDigits(n: number): number {
  if (n >= 1000) return 2
  if (n >= 1) return 4
  if (n >= 0.01) return 4
  return 8
}

function formatPx(n: number): string {
  return formatNumber(n, {
    minimumFractionDigits: Math.min(2, priceDigits(n)),
    maximumFractionDigits: priceDigits(n),
  })
}

function AssetIcon({ icon, className }: { icon: string; className?: string }) {
  const colors: Record<string, string> = {
    aave: 'bg-[#2ebac6]',
    ada: 'bg-[#0033ad]',
    algo: 'bg-[#111111] dark:bg-[#eaecef] dark:text-[#111]',
    btc: 'bg-[#f7931a]',
    eth: 'bg-[#627eea]',
    sol: 'bg-[#9945ff]',
    eu: 'bg-[#003399]',
    gb: 'bg-[#c8102e]',
    aapl: 'bg-[#555555]',
    xau: 'bg-[#d4af37]',
  }
  const labels: Record<string, string> = {
    aave: 'A',
    ada: '₳',
    algo: 'Ⱥ',
    btc: '₿',
    eth: 'Ξ',
    sol: 'S',
    eu: '€',
    gb: '£',
    aapl: '',
    xau: 'Au',
  }
  return (
    <span
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[12px] font-bold text-white shadow-sm ring-1 ring-black/5',
        colors[icon] ?? 'bg-accent',
        className,
      )}
    >
      {labels[icon] ?? icon.slice(0, 1).toUpperCase()}
    </span>
  )
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
      <path
        d="M8 1.5l1.76 3.56 3.93.57-2.84 2.77.67 3.91L8 10.52l-3.52 1.85.67-3.91L2.3 5.63l3.93-.57L8 1.5z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Market list — search, chip filters, bid/ask columns.
 */
export function WatchlistPanel({
  className,
  title: _title,
  navigateOnSelect = false,
}: {
  className?: string
  title?: string
  navigateOnSelect?: boolean
}) {
  const { t } = useTranslation(['trading', 'common'])
  const { instruments, active, setSymbol, favorites, toggleFavorite } =
    useMarket()
  const { setRoute } = useNavigation()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<FilterId>('all')
  const [sortKey, setSortKey] = useState<SortKey>('ticker')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const filters: { id: FilterId; label: string }[] = useMemo(
    () => [
      { id: 'all', label: t('watchlist.filterAll') },
      { id: 'favorites', label: t('watchlist.filterFavorites') },
      { id: 'crypto', label: t('watchlist.filterCrypto') },
      { id: 'forex', label: t('watchlist.filterForex') },
      { id: 'stocks', label: t('watchlist.filterStocks') },
      { id: 'commodities', label: t('watchlist.filterCommodities') },
    ],
    [t],
  )

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = instruments.filter((i) => {
      if (category === 'favorites') {
        if (!favorites.has(i.symbol)) return false
      } else if (category !== 'all' && i.category !== category) {
        return false
      }
      if (!q) return true
      return (
        i.ticker.toLowerCase().includes(q) ||
        i.name.toLowerCase().includes(q) ||
        i.symbol.toLowerCase().includes(q)
      )
    })
    list = [...list].sort((a, b) => {
      const mul = sortDir === 'asc' ? 1 : -1
      if (sortKey === 'ticker') return a.ticker.localeCompare(b.ticker) * mul
      if (sortKey === 'change') return (a.changePct - b.changePct) * mul
      return (a.volume - b.volume) * mul
    })
    return list
  }, [instruments, query, category, sortKey, sortDir, favorites])

  const toggleSort = () => {
    if (sortKey === 'ticker') {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey('ticker')
      setSortDir('asc')
    }
  }

  const onSelect = (row: MarketInstrument) => {
    setSymbol(row.symbol)
    if (navigateOnSelect) setRoute('trade')
  }

  return (
    <section
      className={cn('flex h-full min-h-0 flex-col bg-terminal-bg', className)}
    >
      <div className="shrink-0 space-y-2 border-b border-terminal-border/40 px-2.5 py-2.5">
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 start-2.5 flex items-center text-terminal-muted">
            <SearchIcon />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('watchlist.search')}
            className={cn(
              'h-9 w-full rounded-md border border-terminal-border/50 bg-terminal-surface',
              'ps-8 pe-2.5 text-xs text-terminal-fg placeholder:text-terminal-muted',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
            )}
          />
        </div>
        <div className="flex gap-1 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setCategory(f.id)}
              className={cn(
                'h-7 shrink-0 rounded-md px-2.5 text-[10px] font-semibold transition-colors',
                category === f.id
                  ? 'bg-accent text-white'
                  : 'bg-terminal-elevated text-terminal-muted hover:text-terminal-fg',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className={cn(
          'grid shrink-0 grid-cols-[minmax(0,1.4fr)_0.8fr_0.8fr] items-center',
          'gap-1 border-b border-terminal-border/30 bg-terminal-elevated/70 px-2.5 py-1.5',
          'text-[10px] font-semibold uppercase tracking-wide text-terminal-muted',
        )}
      >
        <button
          type="button"
          onClick={toggleSort}
          className="flex items-center gap-1 text-start hover:text-terminal-fg"
        >
          {t('watchlist.marketCol', { count: rows.length })}
          <SortIcon />
        </button>
        <span className="text-end">{t('watchlist.sellCol')}</span>
        <span className="text-end">{t('watchlist.buyCol')}</span>
      </div>

      <ul className="terminal-scroll min-h-0 flex-1 overflow-y-auto">
        {rows.map((row) => {
          const up = row.changePct >= 0
          const selected = row.symbol === active.symbol
          const fav = favorites.has(row.symbol)
          const bidDown = row.bid < row.open
          const askDown = row.ask < row.open

          return (
            <li key={row.symbol} className="border-b border-terminal-border/25">
              <div
                className={cn(
                  'relative grid grid-cols-[minmax(0,1.4fr)_0.8fr_0.8fr] items-center gap-1 px-2.5 py-2.5',
                  'transition-colors hover:bg-terminal-elevated/45',
                  selected && 'bg-accent/[0.07]',
                )}
              >
                {selected ? (
                  <span className="absolute inset-y-1 start-0 w-0.5 rounded-e bg-accent" />
                ) : null}

                <button
                  type="button"
                  onClick={() => onSelect(row)}
                  className="flex min-w-0 items-start gap-2 text-start"
                >
                  <AssetIcon icon={row.icon} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1">
                      <span className="truncate text-[12px] font-bold tracking-tight text-terminal-fg">
                        {row.ticker}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleFavorite(row.symbol)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleFavorite(row.symbol)
                          }
                        }}
                        className={cn(
                          'shrink-0 rounded p-0.5 transition-colors',
                          fav
                            ? 'text-[#f0b90b]'
                            : 'text-terminal-muted/55 hover:text-terminal-muted',
                        )}
                        aria-label={t('watchlist.favorite')}
                      >
                        <StarIcon filled={fav} />
                      </span>
                    </span>
                    <span className="mt-0.5 block truncate text-[10px] leading-snug text-terminal-muted">
                      {row.name}
                    </span>
                    <span className="mt-1 flex items-center gap-1.5 text-[10px]">
                      <span
                        className={cn(
                          'rounded px-1 py-px font-semibold tabular-nums',
                          up
                            ? 'bg-bid-badge text-bid'
                            : 'bg-danger/10 text-danger',
                        )}
                      >
                        {up ? '▲' : '▼'}{' '}
                        {formatPercentChange(Math.abs(row.changePct))}
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-terminal-muted">
                        <VolumeIcon />
                        {row.volume}
                      </span>
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => onSelect(row)}
                  className="min-w-0 text-end"
                >
                  <span
                    className={cn(
                      'block font-mono text-[12px] font-semibold tabular-nums leading-tight',
                      bidDown ? 'text-danger' : 'text-terminal-fg',
                    )}
                  >
                    {formatPx(row.bid)}
                  </span>
                  <span className="mt-0.5 block font-mono text-[9px] tabular-nums text-terminal-muted">
                    {t('watchlist.openShort')}: {formatPx(row.open)}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => onSelect(row)}
                  className="min-w-0 text-end"
                >
                  <span
                    className={cn(
                      'block font-mono text-[12px] font-semibold tabular-nums leading-tight',
                      askDown ? 'text-danger' : 'text-terminal-fg',
                    )}
                  >
                    {formatPx(row.ask)}
                  </span>
                  <span className="mt-0.5 block font-mono text-[9px] tabular-nums text-terminal-muted">
                    {t('watchlist.highShort')}: {formatPx(row.high)}
                  </span>
                </button>
              </div>
            </li>
          )
        })}
        {rows.length === 0 ? (
          <li className="px-3 py-10 text-center text-xs text-terminal-muted">
            {t('watchlist.empty')}
          </li>
        ) : null}
      </ul>
    </section>
  )
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10.5 10.5L14 14"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SortIcon() {
  return (
    <svg viewBox="0 0 10 12" className="h-3 w-2.5 opacity-60" aria-hidden>
      <path d="M5 1L8 4.5H2L5 1Z" fill="currentColor" />
      <path d="M5 11L2 7.5H8L5 11Z" fill="currentColor" />
    </svg>
  )
}

function VolumeIcon() {
  return (
    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" aria-hidden>
      <path
        d="M2 9V6M5 9V3M8 9V5M11 9V2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  )
}
